import { useState, type ReactNode, useEffect } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useSession, useOwnOnly } from '../../lib/auth/session.js';
import { logout } from '../../lib/api/client.js';
import { Icon, BrandMark, type IconName } from '../ui/Icon.js';
import { UpdateBanner } from './UpdateBanner.js';
import { SuspendedNotice } from './SuspendedNotice.js';
import { ThemeToggle } from './ThemeToggle.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  /** Held, or any one of several (the broad permission or its own form). */
  permission?: string | readonly string[];
  /** Not for somebody who sees only their own seats (useOwnOnly). */
  notOwnOnly?: boolean;
  /**
   * MSP console only. REQUIRED on every platform destination -- a permission
   * alone is not a gate here.
   *
   * BUILTIN_ROLES derives its read-only role as
   * PERMISSION_KEYS.filter(k => k.endsWith(':read')) (contracts/permissions.ts),
   * which hands a TENANT read-only user audit:read, user:read, number:read,
   * billing:read, invoice:read and ratedeck:read. So gating an MSP item on a
   * ':read' permission gates nothing: every one of these was visible to
   * customers, and /platform/settings had no gate at all.
   *
   * The APIs behind them assert platform scope, so no customer data leaked --
   * but showing a customer the operator console is its own problem, and it is
   * the exact split the platform is meant to enforce.
   */
  platformOnly?: boolean;
  /** Client portal only -- realtime supervision, the customer's own PBX. */
  tenantOnly?: boolean;
}
interface NavSection { title: string; items: NavItem[] }

/**
 * Operator console navigation.
 *
 * Accounts, products, inventory, billing. Per-tenant telephony configuration
 * lives behind an account, not up here (doc 24) — two places to change one
 * setting is how they diverge.
 */
const NAV: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { to: '/', label: 'Dashboard', icon: 'dashboard', notOwnOnly: true },
      { to: '/activity', label: 'Activity log', icon: 'clock', permission: 'audit:read', platformOnly: true },
    ],
  },
  {
    /**
     * What is happening RIGHT NOW, as opposed to what happened.
     *
     * Its own section rather than one "Live" item under Overview, because there
     * is more than one live view and they are read in different situations:
     * "is anyone's phone broken" and "is anyone waiting on hold" are different
     * questions asked by different people.
     *
     * Paths do not share a prefix. The sidebar marks an item active with
     * startsWith, so /realtime and /realtime/queues would both light up.
     *
     * TENANT-ONLY, which is the deliberate half of this. Watching phones and
     * queues in real time is a CUSTOMER's tool -- a supervisor deciding whether
     * to pull someone onto the floor. Platform staff open a customer from the
     * account list to answer a commercial or provisioning question, and putting
     * a live wallboard in that path answers a question nobody asked while
     * implying the console is where you go to supervise somebody else's team.
     */
    title: 'Realtime',
    items: [
      { to: '/realtime/extensions', label: 'Extensions', icon: 'extension', permission: 'extension:read', tenantOnly: true },
      { to: '/realtime/queues', label: 'Queues', icon: 'queue', permission: 'queue:read', tenantOnly: true },
    ],
  },
  {
    // A tenant-scoped user has one account and no list to drill through, so
    // their own PBX is surfaced directly.
    title: 'My PBX',
    items: [
      { to: '/my/extensions', label: 'Extensions', icon: 'extension', permission: ['extension:read', 'extension:read:own'], tenantOnly: true },
      { to: '/my/calls', label: 'Call history', icon: 'phone', permission: ['cdr:read', 'cdr:read:own'], tenantOnly: true },
      { to: '/my/billing', label: 'Billing', icon: 'billing', permission: 'billing:read', tenantOnly: true },
    ],
  },
  {
    title: 'Accounts',
    items: [
      { to: '/accounts', label: 'Customer accounts', icon: 'accounts', permission: 'tenant:read', notOwnOnly: true },
      { to: '/resellers', label: 'Resellers', icon: 'resellers', permission: 'reseller:read', platformOnly: true },
      { to: '/users', label: 'Portal users', icon: 'users', permission: 'user:read', platformOnly: true },
    ],
  },
  {
    title: 'Products',
    items: [
      { to: '/products/extension-types', label: 'Extension types', icon: 'extension', permission: 'tenant:read', platformOnly: true },
      { to: '/products/call-plans', label: 'Call plans', icon: 'plan', permission: 'ratedeck:read', platformOnly: true },
      { to: '/products/rate-sheets', label: 'Rate sheets', icon: 'rates', permission: 'ratedeck:read', platformOnly: true },
    ],
  },
  {
    title: 'Inventory',
    items: [
      { to: '/inventory/carriers', label: 'Carriers & trunks', icon: 'carrier', permission: 'carrier:read', platformOnly: true },
      { to: '/inventory/numbers', label: 'DID numbers', icon: 'hash', permission: 'number:read', platformOnly: true },
      { to: '/inventory/handsets', label: 'Handsets', icon: 'phone', permission: 'device:read', platformOnly: true },
      { to: '/inventory/templates', label: 'Phone templates', icon: 'plan', permission: 'device:read', platformOnly: true },
    ],
  },
  {
    title: 'Billing',
    items: [
      { to: '/billing/runs', label: 'Billing runs', icon: 'billing', permission: 'billing:read', platformOnly: true },
      { to: '/billing/invoices', label: 'Invoices', icon: 'invoice', permission: 'invoice:read', platformOnly: true },
      { to: '/billing/setup', label: 'Billing setup', icon: 'billing', permission: 'billing:write', platformOnly: true },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: '/platform/nodes', label: 'Nodes', icon: 'server', permission: 'node:read', platformOnly: true },
      { to: '/platform/jobs', label: 'Scheduled jobs', icon: 'clock', permission: 'node:read', platformOnly: true },
      { to: '/platform/provider-keys', label: 'Provider keys', icon: 'settings', permission: 'providerkey:read', platformOnly: true },
      { to: '/platform/recording-storage', label: 'Recording storage', icon: 'media', permission: 'node:read', platformOnly: true },
      { to: '/platform/sip-security', label: 'SIP security', icon: 'globe', permission: 'node:read', platformOnly: true },
      { to: '/platform/settings', label: 'Settings', icon: 'settings', platformOnly: true },
    ],
  },
];

export function AppShell({ children }: { children: ReactNode }) {
  const session = useSession((s) => s.session);
  const can = useSession((s) => s.can);
  const clear = useSession((s) => s.clear);
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('arc.nav.collapsed') === '1',
  );
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const isPlatform = session?.scope.level === 'platform';
  const ownOnly = useOwnOnly();
  const isTenantScoped = session?.scope.level === 'tenant' || session?.scope.level === 'site';

  const toggle = () => {
    setCollapsed((v) => {
      try { localStorage.setItem('arc.nav.collapsed', v ? '0' : '1'); } catch { /* ignore */ }
      return !v;
    });
  };

  const visible = (i: NavItem): boolean => {
    if (i.permission && !(typeof i.permission === 'string' ? [i.permission] : i.permission).some(can)) return false;
    if (i.notOwnOnly && ownOnly) return false;
    if (i.platformOnly && !isPlatform) return false;
    if (i.tenantOnly && !isTenantScoped) return false;
    return true;
  };

  /**
   * A reseller's brand (migration 0087): its colour replaces ArcNet's green across the portal for
   * its people and its clients, and its name titles the tab. Set on the root, where the tokens
   * live, and taken off again for anyone without one.
   */
  const brand = session?.brand;
  const level = session?.scope.level;
  useEffect(() => {
    const root = document.documentElement.style;
    const names = ['--arc-brand', '--arc-accent', '--arc-accent-hover', '--arc-brand-from', '--arc-brand-to',
      '--arc-nav-accent'];
    if (brand?.custom) {
      root.setProperty('--arc-brand', brand.accent);
      root.setProperty('--arc-accent', brand.accent);
      root.setProperty('--arc-accent-hover', brand.accent);
      root.setProperty('--arc-brand-from', brand.gradient_from ?? brand.accent);
      root.setProperty('--arc-brand-to', brand.gradient_to ?? brand.accent);
      root.setProperty('--arc-nav-accent', brand.gradient_to ?? brand.accent);
    } else {
      for (const n of names) root.removeProperty(n);
    }
    // The console for staff; the TMS for a client's own people (owner, 18 September 2026).
    document.title = `${brand?.name ?? 'ArcNet'} - ${level === 'tenant' ? 'TMS' : 'Management Console'}`;
  }, [brand?.custom, brand?.accent, brand?.gradient_from, brand?.gradient_to, brand?.name, level]);

  const initials = (session?.user.full_name ?? session?.user.email ?? '?')
    .split(/[\s@.]+/).filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('');

  return (
    <div className="arc-app">
      <aside className="arc-sidebar" data-collapsed={collapsed}>
        <div className="arc-brand">
          <BrandMark size={26} brand={session?.brand} />
          {!collapsed && (
            <div className="arc-stack">
              <span>{session?.brand?.name ?? 'ArcNet'}</span>
              <span className="arc-brand-sub">Console</span>
            </div>
          )}
        </div>

        <nav className="arc-nav" aria-label="Main">
          {NAV.map((section) => {
            const items = section.items.filter(visible);
            if (items.length === 0) return null;
            return (
              <div key={section.title}>
                {!collapsed && <div className="arc-nav-section">{section.title}</div>}
                {items.map((item) => {
                  const active = item.to === '/'
                    ? pathname === '/'
                    : pathname.startsWith(item.to);
                  return (
                    <Link
                      key={item.to} to={item.to} className="arc-nav-item"
                      data-active={active} title={collapsed ? item.label : undefined}
                    >
                      <Icon name={item.icon} size={18} />
                      {!collapsed && <span>{item.label}</span>}
                    </Link>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="arc-sidebar-foot">
          <button
            type="button" className="arc-nav-item" onClick={toggle}
            style={{ width: '100%', border: 'none', background: 'none', font: 'inherit' }}
            aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
          >
            <Icon name="panelLeft" size={16} />
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>
      </aside>

      <div className="arc-main">
        <header className="arc-header">
          <ScopeIndicator />
          <div className="arc-spacer" />

          {session?.impersonated_by && (
            <span className="arc-badge arc-badge-warn" role="status">
              <Icon name="warning" size={11} />
              Impersonating — actions are audited
            </span>
          )}

          <ThemeToggle />

          <div className="arc-row" style={{ gap: 7 }}>
            {/* Your own account and its security (two-step verification). */}
            <Link to="/account/security" className="arc-row" title="Your account"
              style={{ gap: 7, color: 'inherit', textDecoration: 'none' }}>
              <div className="arc-avatar" aria-hidden="true">{initials}</div>
              <div className="arc-stack" style={{ lineHeight: 1.25 }}>
                <span style={{ fontSize: 12.5, fontWeight: 550 }}>
                  {session?.user.full_name ?? session?.user.email}
                </span>
                <span className="arc-subtle" style={{ fontSize: 11 }}>
                  {session?.memberships[0]?.role_name ?? '—'}
                </span>
              </div>
            </Link>
            <button
              type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
              onClick={() => { void logout().then(clear); }}
              title="Sign out" aria-label="Sign out"
            >
              <Icon name="logout" size={15} />
            </button>
          </div>
        </header>

        <main className="arc-content">
          <div className="arc-page-max"><SuspendedNotice />{children}</div>
        </main>

        <UpdateBanner />
      </div>
    </div>
  );
}

function ScopeIndicator() {
  const session = useSession((s) => s.session);
  if (!session) return null;
  const label =
    session.scope.level === 'platform' ? 'Platform'
    : session.scope.level === 'reseller' ? 'Reseller'
    : session.memberships[0]?.tenant_name ?? 'Tenant';
  return (
    <div className="arc-row" style={{ gap: 7 }}>
      <span className="arc-badge arc-badge-info">
        <span className="arc-dot" aria-hidden="true" />
        {label}
      </span>
      {session.scope.all_tenants && (
        <span className="arc-subtle" style={{ fontSize: 12 }}>all accounts</span>
      )}
    </div>
  );
}
